import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Phone, Loader2, ArrowRight, MessageSquare } from "lucide-react";

function timeAgo(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short" });
}

function urgencyClass(at) {
  if (!at) return "text-muted-foreground";
  const hours = (Date.now() - new Date(at).getTime()) / 3_600_000;
  if (hours > 24) return "text-destructive";
  if (hours > 4) return "text-warning";
  return "text-foreground";
}

/**
 * Workshop-level inbox of every customer waiting on an advisor callback.
 * Lives at the top of the Workshop Control tab — fastest decay-of-revenue
 * tile in the dashboard, oldest-waiting first so we serve customers fairly.
 */
export default function CallbackQueuePanel() {
  const [data, setData] = useState({ count: 0, items: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/controller/callback-queue");
      setData(r.data);
    } catch {
      /* ignored */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60_000); // poll every 60s
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <div className="card-default p-5" data-testid="callback-queue-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="inline-flex w-9 h-9 rounded-full bg-primary/15 items-center justify-center">
            <Phone className="w-4 h-4 text-primary" />
          </span>
          <div>
            <div className="label-overline">Callback queue</div>
            <h2 className="font-display font-bold text-lg tracking-tight">
              {data.count === 0 ? "All clear" : `${data.count} customer${data.count === 1 ? "" : "s"} waiting`}
            </h2>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              Friendly auto-nudge fires if a wait crosses 15 minutes.
            </div>
          </div>
        </div>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </div>

      {data.count === 0 && !loading && (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="callback-queue-empty">
          Nobody's asked for a call back. When a customer taps "Speak to an advisor" on their quote they'll appear here.
        </p>
      )}

      {data.items.length > 0 && (
        <ul className="mt-4 divide-y divide-border" data-testid="callback-queue-list">
          {data.items.map((row) => (
            <li
              key={`${row.job_id}-${row.callback_idx}`}
              className="py-3 flex flex-wrap items-start gap-3 justify-between"
              data-testid={`callback-queue-row-${row.job_id}-${row.callback_idx}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-semibold truncate">{row.customer_name || "Unnamed customer"}</span>
                  <span className="text-xs text-muted-foreground">
                    {row.vehicle_year} {row.vehicle_make} {row.vehicle_model}
                    {row.vehicle_reg ? ` · ${row.vehicle_reg}` : ""}
                  </span>
                </div>
                <div className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={urgencyClass(row.at)}>
                    Waiting <strong>{timeAgo(row.at)}</strong>
                  </span>
                  <span className="text-muted-foreground">
                    Prefers <strong className="text-foreground">{(row.preferred_time || "asap").toUpperCase()}</strong>
                  </span>
                  {row.item_count > 0 && (
                    <span className="text-muted-foreground truncate">
                      About: {row.item_names.slice(0, 2).join(", ")}{row.item_count > 2 ? ` +${row.item_count - 2}` : ""}
                    </span>
                  )}
                  {row.nudge_sent_at && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-warning/15 text-warning text-[10px] font-bold uppercase tracking-wider"
                      title={`Auto-nudge sent at ${new Date(row.nudge_sent_at).toLocaleString("en-GB")}`}
                      data-testid={`callback-queue-nudged-${row.job_id}-${row.callback_idx}`}
                    >
                      Auto-nudged
                    </span>
                  )}
                </div>
                {row.customer_note && (
                  <div className="text-xs mt-1.5 text-muted-foreground italic flex gap-1 max-w-2xl">
                    <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    "{row.customer_note}"
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {row.customer_phone && (
                  <a
                    href={`tel:${row.customer_phone.replace(/\s/g, "")}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 whitespace-nowrap"
                    data-testid={`callback-queue-call-${row.job_id}-${row.callback_idx}`}
                  >
                    <Phone className="w-3 h-3" /> Call
                  </a>
                )}
                <Link
                  to={`/jobs/${row.job_id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-border hover:bg-muted text-xs font-semibold whitespace-nowrap"
                  data-testid={`callback-queue-open-${row.job_id}-${row.callback_idx}`}
                >
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
